import { useEffect, useState } from "react";

import {
    DatePicker, Button, RadioButton, TextArea, TextField,
    Table, TableMetadata,
    Typography, useTable, AutocompleteMultipleSelect, Label, InfoPanel
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Container, Modal as BSModal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
    getCommitteeById, createCountyCommitteeById, searchEmployee,
    saveCommittee, getEmployeeOib
} from "../../api/Committee/CommitteeApi";
import { useAppDispatch } from "../../app/store";
import { ContentArea, FormCard, ToolbarEdit, ToolbarTop } from "../../components";
import { useAuthCounties } from "../../hooks/auth";
import { ValidationFailure } from "../../models";
import { Committee, CommitteeStatus, CommitteeEmployee, OibEmployee } from "../../models/Committee";
import { CodeBookPagedFilter } from "../../models/Shared";
import { countyCommitteeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, MsgUtils } from "../../utils";

export function CountyCommitteeEdit() {
    const { id, countyId } = useParams<{ id: string, countyId: string }>();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const counties = useAuthCounties();
    const oibRegex = /^\d{11}$/;

    const createDefaultItem = (): Committee => {
        return {
            id: 0,
            committeeTypeId: 2,
            statusId: CommitteeStatus.NotDefined,
            isSelected: false,
        };
    };
    const [item, setItem] = useState<Committee>(createDefaultItem);
    const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
    const tableMetadata: TableMetadata<CommitteeEmployee> = {
        rowKey: "id",
        columns: [
            {
                headerClassName: "col-4",
                columnKey: "expertCommissionRole",
                header: t("common.expertCommission.role"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{item.committeeRole?.name}</div>)
                },
            },
            {
                headerClassName: "col-5",
                columnKey: "employee",
                header: t("common.expertCommission.employee"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{getCommitteeEmployeeDisplayName(item)}</div>)
                },
            },
            {
                headerClassName: "col-1",
                columnKey: "edit",
                header: t("common.edit"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div className={"text-right"}>
                        <Button
                            size="sm"
                            outline={true}
                            onClick={() => handleDetailsClicked(item.committeeRole!.id, item.committeePosition!.id)}
                        >
                            {t("common.edit")}
                        </Button>
                    </div>)
                }
            },
            {
                headerClassName: "col-1",
                columnKey: "expertCommissionPosition",
                header: t("common.expertCommission.position"),
                accessor: (item: CommitteeEmployee) => {
                    return (<div>{item.committeePosition?.name}</div>)
                },
            },
            {
                headerClassName: "col-1",
                columnKey: "president",
                header: t("common.expertCommission.president"),
                accessor: (emp: CommitteeEmployee) => {
                    return (
                        <div className="pt-2 ml-3">
                            <RadioButton
                                ariaLabel="item.president"
                                key={emp.committeeRole?.id}
                                name={"item.president"}
                                checked={emp.isPresident}
                                onChange={(e) => handlePresidentChange(emp.committeeRole.id, emp.committeePosition!.id)}
                                value={emp.committeeRole!.id}
                                disabled={!emp.employee}
                            />
                        </div>
                    )
                },
            },
        ],
        rowToolTip: () => undefined
    };

    const [tableProps] = useTable(
        {
            metadata: tableMetadata,
            data: tableData,
            pagination: false,
            paginationPage: undefined,
            paginationPageSize: undefined,
            totalItems: 1,
            defaultSort: {}
        }
    );
    const [errors, setErrors] = useState<ValidationFailure[]>()
    const [filteredEmployees, setFilteredEmployees] = useState<CodeBook[]>([]);
    const [statuses] = useState<CodeBook[]>(MsgUtils.getEnumToCodeBook(CommitteeStatus, "CommitteeStatus"))
    const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
    const [employee, setEmployee] = useState<CodeBook>();
    const [oibEmployee, setOibEmployee] = useState<OibEmployee>();
    const [showOibAlert, setShowOibAlert] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            if (!isNaN(+id) && +id > 0) {
                let result = (await getCommitteeById(+id));
                if (result) {
                    setItem(result);
                    setTableData(result.employees!)
                }
            }
            if (!isNaN(+countyId) && +countyId > 0) {
                let result = (await createCountyCommitteeById(+countyId));
                if (result) {
                    setItem(result);
                    setTableData(result.employees!)
                }
            }
        })();
        dispatch(resetIsEdited())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, countyId]);

    const handleDetailsClicked = (expertCommissionRoleId: number, positionId: number) => {
        var expertCommissionEmployee = tableData.find(value => value.committeeRole.id === expertCommissionRoleId && value.committeePosition?.id === positionId);
        if (expertCommissionEmployee) {
            setEditing(expertCommissionEmployee);
            setEmployee(expertCommissionEmployee.employee);
            setOibEmployee(expertCommissionEmployee.oibEmployee);
        }
    };
    const handleDateFromChangeAny = (e: any) => {
        var dateFrom = new Date(e.target.value);
        const newItem: Committee = {
            ...item,
            dateFrom: dateFrom,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleDateToChangeAny = (e: any) => {
        var dateTo = new Date(e.target.value);
        const newItem: Committee = {
            ...item,
            dateTo: dateTo,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleEmployeeFilterChange = async (filter: string) => {
        const search: CodeBookPagedFilter = {
            pageSize: 100,
            page: 1,
            search: filter,
        };
        var result = await searchEmployee(search);
        setFilteredEmployees(result.results);
    };
    const handleEmployeeChangeMultiselect = (e: any) => {
        const employeeList = e.target.value as CodeBook[];
        const employeeCodeBook = employeeList.length > 0 ? employeeList[0] : undefined;
        setEmployee(employeeCodeBook);
        if (employeeCodeBook) {
            setOibEmployee(undefined);
        }
    };
    const handlePresidentChange = (roleId: number, positionId: number) => {
        const employees = tableData.map(val => {
            val.isPresident = val.committeeRole.id === roleId && val.committeePosition?.id === positionId;
            return val;
        });

        const newItem: Committee = {
            ...item,
            employees: employees,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleNoteChange = (e: any) => {
        const note = e.target.value;
        const newItem: Committee = {
            ...item,
            note: note,
        };
        setItem(newItem);
        dispatch(setIsEdited());
    };
    const handleOibChanged = (e: any) => {
        const oib = e.target.value as string;
        const newOibEmployee: OibEmployee = {
            ...oibEmployee,
            oib: oib,
            lastName: undefined,
        };
        setOibEmployee(newOibEmployee);
        setShowOibAlert(null);
    };
    const handleOibSystemClick = async () => {
        if (oibEmployee?.oib) {
            var result = (await getEmployeeOib(oibEmployee.oib));
            if (result && result.item && result.item.oib) {
                const newOibEmployee: OibEmployee = {
                    ...oibEmployee,
                    firstName: result.item.firstName,
                    lastName: result.item.lastName,
                };
                setOibEmployee(newOibEmployee);
                setEmployee(undefined);
            } else {
                setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
            }
        }
    };
    const getCommitteeEmployeeDisplayName = (ce: CommitteeEmployee) => {
        var name = undefined;
        if (ce.employee) {
            name = ce.employee.name;
        } else if (ce.oibEmployee) {
            name = `${ce.oibEmployee.oib}, ${ce.oibEmployee.lastName}, ${ce.oibEmployee.firstName}`;
        }
        return name;
    };
    return (
        <ContentArea>
            <ToolbarTop
                showAdd={"hide"}
                showBack={"show"}
                showEdit={"hide"}
                pageTitle={t("common.expertCommission.countyPageNameEdit")}
                errors={errors}
            />
            <FormCard>
                <Row>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.expertCommission.county")}
                        </Typography>
                    </Col>
                    <Col md={10}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {counties.find(c => c.id === item.countyId)?.name}
                        </Typography>
                    </Col>
                </Row>
                <Row>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.expertCommission.dateFromDateTo")}
                        </Typography>
                    </Col>
                    <Col md={4}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {item.dateFrom ? hrDate(item.dateFrom) : ""} - {item.dateTo ? hrDate(item.dateTo) : ""}
                        </Typography>
                    </Col>
                    <Col md={2}>
                        <Typography
                            variant="base"
                            color="light"
                        >
                            {t("common.status")}
                        </Typography>
                    </Col>
                    <Col md={4}>
                        <Label color={FormatUtils.getCommitteeStatusColor(item.statusId)} className="text-center" outline={false} fullWidth={false}>
                            {statuses.find(v => v.id === item.statusId)?.name}
                        </Label>
                    </Col>
                </Row>
            </FormCard>

            <FormCard>
                <Table {...tableProps} />
            </FormCard>

            <FormCard>
                <Row>
                    <Col md={12}>
                        <TextArea
                            ariaLabel="item.note"
                            label={t("common.expertCommission.note")}
                            name={"item.note"}
                            value={item.note}
                            onChange={handleNoteChange}
                            errorText={errors?.find(e => e.propertyName === "Note")?.message}
                            maxLength={5000}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={3}>
                        <DatePicker
                            label={t("common.expertCommission.dateFrom")}
                            name={"expertCommission.dateFrom"}
                            onChange={handleDateFromChangeAny}
                            disabled={item.dateTo && item.dateTo < new Date()}
                            value={item.dateFrom}
                            minYear={(new Date()).getFullYear() - 4}
                            maxYear={(new Date()).getFullYear() + 5}
                            errorText={errors?.find(e => e.propertyName === "DateFrom")?.message}
                        />
                    </Col>
                    <Col md={3}>
                        <DatePicker
                            label={t("common.expertCommission.dateTo")}
                            name={"expertCommission.dateTo"}
                            onChange={handleDateToChangeAny}
                            disabled={item.dateTo && item.dateTo < new Date()}
                            value={item.dateTo}
                            minYear={(new Date()).getFullYear() - 4}
                            maxYear={(new Date()).getFullYear() + 5}
                            errorText={errors?.find(e => e.propertyName === "DateTo")?.message}
                        />
                    </Col>
                    <Col md={6}>

                    </Col>
                </Row>
            </FormCard>

            <ToolbarEdit
                showCancel={"show"}
                showSave={"show"}
                handleSave={async () => saveCommittee(item)}
                redirectUrlSaveOK={countyCommitteeDetailUrl}
                setSaveErrors={setErrors}
            />
            <BSModal
                size="lg"
                backdrop="static"
                centered
                animation={false}
                show={editing != null}
                onConfirm={() => setEditing(null)}
                onClose={() => setEditing(null)}
                onHide={() => setEditing(null)}
            >
                <BSModal.Header>
                    {editing?.committeeRole!.name}
                </BSModal.Header>
                <BSModal.Body>
                    <Container>
                        {showOibAlert === null || <InfoPanel color="danger" message="" dismissible={false} fullWidth={true} >{showOibAlert}</InfoPanel>}
                        <Row>
                            <Col md={9}>
                                <AutocompleteMultipleSelect
                                    label={t("common.expertCommission.employee")}
                                    name={"employeeId"}
                                    value={employee?.id !== undefined ? [employee] : []}
                                    valueProp={"id"}
                                    textProp={"name"}
                                    onChange={handleEmployeeChangeMultiselect}
                                    selectOptions={filteredEmployees}
                                    onFilterChange={handleEmployeeFilterChange}
                                    maximumSelectedItems={1}
                                />
                            </Col>
                        </Row>
                        {editing?.committeeRole.checkOIB &&
                            (<>
                                <Row>
                                    <Col md={8}>
                                        <TextField
                                            label={t("common.schoolCandidateModel.oib")}
                                            value={oibEmployee?.oib}
                                            onChange={handleOibChanged}
                                            disabled={employee?.id !== undefined}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <br />
                                        <Button onClick={handleOibSystemClick}
                                            disabled={!(oibEmployee?.oib !== undefined && oibRegex.test(oibEmployee.oib))}
                                            size="sm">
                                            {t("common.schoolCandidateModel.connectOibSystem")}
                                        </Button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <div>{oibEmployee?.lastName !== undefined ? `${oibEmployee?.oib}, ${oibEmployee?.lastName}, ${oibEmployee?.firstName}` : ""}</div>
                                    </Col>
                                </Row>
                            </>)
                        }
                    </Container>
                </BSModal.Body>
                <BSModal.Footer>
                    <Button onClick={() => {
                        setEmployee(undefined);
                        setOibEmployee(undefined);
                        setEditing(null);
                        setFilteredEmployees([]);
                        setShowOibAlert(null);
                    }}
                        size="sm"
                        outline={true}>
                        {t("common.quit")}
                    </Button>
                    <Button onClick={() => {
                        editing!.employee = employee && employee.id ? employee : undefined;
                        editing!.oibEmployee = oibEmployee && oibEmployee.lastName ? oibEmployee : undefined;
                        if (editing!.committeeRole.id === 20) {//default for lječnik
                            let presidentExist = false;
                            tableData.forEach((el) => presidentExist = el.isPresident || presidentExist);
                            editing!.isPresident = !presidentExist;
                        }
                        setFilteredEmployees([]);
                        setEmployee(undefined);
                        setOibEmployee(undefined);
                        dispatch(setIsEdited());
                        setEditing(null);
                        setShowOibAlert(null);
                    }}
                        size="sm">
                        {t("common.save")}
                    </Button>
                </BSModal.Footer>
            </BSModal>
        </ContentArea>
    );
}